import React, { useCallback, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Table,
  Button,
  Space,
  InputNumber,
  Checkbox,
  message,
  Drawer,
  Input,
  Typography,
  Tooltip,
  Select,
  Modal,
  Popover,
  Tag,
  Alert,
} from "antd";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { apiConfig, apiTokenRequest } from "../authConfig";
import { useApp } from "../context/AppContext";
import { canApproveRiskRegister } from "../utils/approvalGates";
import { Role } from "../constants/roles";
import { pickScopingPersistPayload } from "../utils/pickScopingPersistPayload";
import { listUnsetSoaControls, normalizeSoaApplicable, plainSoaGateMessage } from "../utils/soaApplicability";
import { SoaApplicabilityBadge, SoaUnsetNotice } from "./SoaUnsetNotice";
import { SoaApprovalPanel } from "./SoaApprovalPanel";
import { isSoaApproved, parseSoaApprovalError, isSoaLockedError, stripSoaServerOwnedFields, soaPostApprovalEditsText } from "../utils/soaApproval";
import DraggableHeaderCell from "./DraggableHeaderCell";
import { RiskRowLifecycleActions, RiskOpenClosedBadge, useRiskHistory } from "./RiskLifecycle";
import { formatDdMmYyyy, formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import {
  normalizeRiskScale,
  computeRiskRegisterScore,
  formatSeverityRankTooltip,
  formatLikelihoodRankTooltip,
  formatConsequenceRankTooltip,
  ciaConsequenceBinarySumFromRow,
} from "../constants/riskAnalysisMatrix";
import SubStepNav from "./page/SubStepNav";
import StageHeader from "./page/StageHeader";

const { TextArea } = Input;
const { Text } = Typography;

const RR_COL_WIDTH_STORAGE_KEY = "riskRegisterTableColumnWidths";
const RR_COL_ORDER_STORAGE_KEY = "riskRegisterTableColumnOrder";
const RT_COL_WIDTH_STORAGE_KEY = "riskTreatmentTableColumnWidths";
const RT_COL_ORDER_STORAGE_KEY = "riskTreatmentTableColumnOrder";

const REGISTER_PINNED_LEFT_KEY = "riskId";
const REGISTER_PINNED_RIGHT_KEY = "act";
const REGISTER_MIDDLE_COLUMN_KEYS = [
  "riskTitle",
  "department",
  "riskOwnerUserId",
  "riskCategory",
  "riskSource",
  "riskDomain",
  "severityRank",
  "likelihoodRank",
  "consequenceRank",
  "riskScore",
  "riskSignificanceCategory",
];

const TREATMENT_PINNED_LEFT_KEY = "tr_riskId";
const TREATMENT_MIDDLE_COLUMN_KEYS = [
  "tr_title",
  "tr_owner",
  "tr_category",
  "tr_score",
  "tr_asset",
  "tr_strategy",
  "tr_controls",
  "tr_revL",
  "tr_revC",
  "tr_revS",
  "tr_revScore",
  "tr_revSig",
  "tr_acceptable",
  "tr_start",
  "tr_end",
  "tr_recurrent",
  "tr_freq",
];

function normalizeColumnOrder(saved, canonKeys) {
  if (!Array.isArray(saved)) return [...canonKeys];
  const allowed = new Set(canonKeys);
  const seen = new Set();
  const out = [];
  for (const k of saved) {
    if (allowed.has(k) && !seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  for (const k of canonKeys) {
    if (!seen.has(k)) out.push(k);
  }
  return out;
}

function loadJsonColumnOrder(storageKey, canonKeys) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [...canonKeys];
    return normalizeColumnOrder(JSON.parse(raw), canonKeys);
  } catch {
    return [...canonKeys];
  }
}

/** Loads treatment column order; migrates older saves missing `tr_owner` to place it after Title. */
function loadTreatmentColumnOrder() {
  try {
    const raw = localStorage.getItem(RT_COL_ORDER_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    const savedArr = Array.isArray(saved) ? saved : [];
    const hadTrOwnerInSave = savedArr.includes("tr_owner");
    let ordered = normalizeColumnOrder(savedArr, TREATMENT_MIDDLE_COLUMN_KEYS);
    if (!hadTrOwnerInSave && ordered.includes("tr_owner")) {
      ordered = ordered.filter((k) => k !== "tr_owner");
      const ti = ordered.indexOf("tr_title");
      if (ti >= 0) ordered.splice(ti + 1, 0, "tr_owner");
      else ordered = ["tr_owner", ...ordered];
      ordered = normalizeColumnOrder(ordered, TREATMENT_MIDDLE_COLUMN_KEYS);
    }
    return ordered;
  } catch {
    return [...TREATMENT_MIDDLE_COLUMN_KEYS];
  }
}

function loadTreatmentWidths() {
  try {
    const raw = localStorage.getItem(RT_COL_WIDTH_STORAGE_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === "object" && !Array.isArray(p) ? p : {};
  } catch {
    return {};
  }
}

/** A blank owner is shown as such; no default project member is ever substituted. */
const OWNER_NOT_ASSIGNED = "Owner not assigned";

/** Display name for risk register owner (member lookup + fallback string). */
function registerOwnerDisplayName(registerRow, projectMembers) {
  const r = registerRow;
  if (!r) return OWNER_NOT_ASSIGNED;
  if (r.riskOwnerUserId && Array.isArray(projectMembers)) {
    const m = projectMembers.find((x) => x.id === r.riskOwnerUserId);
    if (m) return (m.name || m.email || m.id || "").trim() || OWNER_NOT_ASSIGNED;
  }
  const o = String(r.riskOwner ?? "").trim();
  return o || OWNER_NOT_ASSIGNED;
}

function cmpLocaleStr(a, b) {
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { sensitivity: "base" });
}

/**
 * Risk-heat-map color for the register-only additive score (Stage 6, RA — CLAUDE.md §4 item 6).
 * The codebase's only existing significance bands here are binary — see
 * `RISK_REGISTER_SIGNIFICANCE_SUM_THRESHOLD_BY_SCALE` / `computeRiskRegisterScore` in
 * riskAnalysisMatrix.js, which classify strictly as "Significant" vs "Not significant" per
 * scale (no Critical/High/Medium tiers exist in this codebase's data model — do not invent
 * them here). Colors follow the same red/green convention EvidencingMonitoring.jsx's
 * SEVERITY_COLORS map uses for its Low/Critical ends.
 */
function riskSignificanceTagColor(category) {
  if (category === "Significant") return "red";
  if (category === "Not significant") return "green";
  return "default";
}

const REGISTER_FILTER_EMPTY = "__EMPTY__";

const RR_TEMPLATE = "PLITS_RR_V1";
const RT_TEMPLATE = "PLITS_RT_V1";
const SOA_TEMPLATE = "PLITS_SOA_V1";

const TREATMENT_STRATEGY_OPTIONS = [
  { value: "treat", label: "Treat" },
  { value: "tolerate", label: "Tolerate" },
  { value: "terminate", label: "Terminate" },
  { value: "transfer", label: "Transfer" },
];

const TREATMENT_FREQ_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "bi-annual", label: "Bi-annual" },
  { value: "annual", label: "Annual" },
];

const TREATMENT_CONTROLS_VISIBLE_CHIPS = 8;

/** Annex A style "5.1 — Official name", Policy Hub pipe lines, or spaced "5.1 Name" → number + name. */
function parseAnnexControlRef(fullText) {
  const s = String(fullText ?? "").trim();
  if (!s) return { number: null, name: null, raw: s };
  const pipe = s.match(/^(\d+(?:\.\d+)*)\s*\|\s*[^|]*\s*\|\s*[^|]*\s*\|\s*(.+)$/);
  if (pipe) {
    return { number: pipe[1].trim(), name: pipe[2].trim(), raw: s };
  }
  const m = s.match(/^(\d+(?:\.\d+)*)\s*[-—–]\s*(.+)$/);
  if (m) {
    return { number: m[1].trim(), name: m[2].trim(), raw: s };
  }
  const sp = s.match(/^(\d+(?:\.\d+)*)\s+(.{3,})$/);
  if (sp) {
    return { number: sp[1].trim(), name: sp[2].trim(), raw: s };
  }
  return { number: null, name: null, raw: s };
}

/**
 * Short label for a treatment control string (usually an ISO/Annex ref). Full string is the prose description.
 */
function treatmentControlShortLabel(fullText, indexZeroBased) {
  const s = String(fullText ?? "").trim();
  if (!s) return "-";
  const parsed = parseAnnexControlRef(s);
  if (parsed.number) return parsed.number;
  const iso = s.match(/\bISO\s+[\d.]+(?:\s*\/\s*[\d.]+)?\b/i);
  if (iso) return iso[0].trim();
  const annex = s.match(/\bAnnex\s*A\.?\s*[\d.]+\b/i);
  if (annex) return annex[0].trim();
  const leadNum = s.match(/^(?:A\.)?(\d+(?:\.\d+)*)\b/);
  if (leadNum) return leadNum[1];
  return `#${indexZeroBased + 1}`;
}

function TreatmentControlTag({ fullText, indexZeroBased }) {
  const full = String(fullText ?? "").trim();
  if (!full) return null;
  const parsed = parseAnnexControlRef(full);
  const short =
    parsed.number != null && parsed.number !== "" ? parsed.number : treatmentControlShortLabel(full, indexZeroBased);
  return (
    <Popover
      title="Control"
      trigger="click"
      content={
        <p className="m-0 max-w-md text-sm font-medium leading-relaxed text-[color:var(--color-text-hero)]">
          {parsed.name != null && parsed.name !== ""
            ? `${parsed.number ? `${parsed.number} - ` : ""}${parsed.name}`
            : full}
        </p>
      }
    >
      <Tag className="m-0 max-w-[7.5rem] cursor-pointer truncate text-xs hover:border-[color:var(--color-accent)]" title="Click for full text">
        {short}
      </Tag>
    </Popover>
  );
}

function riskRegisterColumnKey(col) {
  if (col.dataIndex != null) return String(col.dataIndex);
  if (col.key != null) return String(col.key);
  return "";
}

function emptyManualRow(scale) {
  const base = {
    rowUid: crypto.randomUUID(),
    riskId: "",
    riskTitle: "",
    riskDescription: "",
    department: "",
    riskOwner: "",
    assetId: "",
    assetName: "",
    riskCategory: "",
    riskSource: "",
    linkedEvidence: "",
    riskDomain: "",
    customerImpact: "",
    financialImpact: "",
    healthSafety: "",
    impactOnReputation: "",
    legalImpact: "",
    severityRank: 3,
    likelihoodRank: 3,
    confidentiality: "",
    integrity: "",
    availability: "",
    privacy: "",
    confidentialityAffected: "N",
    integrityAffected: "N",
    availabilityAffected: "N",
    privacyAffected: "N",
    consequenceRank: 0,
    existingControl: "",
    existingControlStrength: 3,
    riskScore: 0,
    riskSignificanceCategory: "",
    lifecycle: "manual",
    sourceRefs: [],
  };
  const sc = computeRiskRegisterScore(base, scale);
  return { ...base, ...sc };
}

function renumberRiskIds(rows) {
  let n = 100;
  return rows.map((r) => {
    if (r.lifecycle === "rejected") {
      return { ...r, riskId: "" };
    }
    return { ...r, riskId: `R-${n++}` };
  });
}

/** Same semantics as backend treatmentRegisterRowsForPlan — includes legacy rows missing lifecycle. */
function rowEligibleForTreatmentPlan(r) {
  const lc = String(r.lifecycle ?? "").trim().toLowerCase();
  if (lc === "rejected") return false;
  if (lc === "ai_proposed") return false;
  if (lc === "accepted" || lc === "manual") return true;
  const t = String(r.riskTitle ?? "").trim();
  const d = String(r.riskDescription ?? "").trim();
  return !!(t || d);
}

function normalizePkg(raw) {
  if (!raw || typeof raw !== "object") {
    return { templateVersion: RR_TEMPLATE, workflow: "draft", rows: [] };
  }
  const rows = Array.isArray(raw.rows) ? raw.rows : [];
  return {
    templateVersion: raw.templateVersion || RR_TEMPLATE,
    workflow: raw.workflow || "draft",
    lastGeneratedAt: raw.lastGeneratedAt,
    inputFingerprint: raw.inputFingerprint,
    generationOptions: raw.generationOptions,
    submittedToDelegateAt: raw.submittedToDelegateAt,
    submittedToSpocAt: raw.submittedToSpocAt,
    submittedForReviewAt: raw.submittedForReviewAt,
    reviewApprovedAt: raw.reviewApprovedAt,
    reviewApprovedBy: raw.reviewApprovedBy,
    rows,
  };
}

function normalizeTreatmentPkg(raw) {
  if (!raw || typeof raw !== "object") {
    return { templateVersion: RT_TEMPLATE, rows: [] };
  }
  const rows = Array.isArray(raw.rows) ? raw.rows : [];
  return {
    templateVersion: raw.templateVersion || RT_TEMPLATE,
    lastGeneratedAt: raw.lastGeneratedAt,
    inputFingerprint: raw.inputFingerprint,
    publishedAt: raw.publishedAt,
    rows,
  };
}

function normalizeSoaPkg(raw) {
  if (!raw || typeof raw !== "object") {
    return { templateVersion: SOA_TEMPLATE, rows: [] };
  }
  const rowsIn = Array.isArray(raw.rows) ? raw.rows : [];
  const rows = rowsIn
    .map((r) => {
      if (!r || typeof r !== "object") return null;
      const controlId = String(r.controlId ?? "").trim();
      if (!controlId) return null;
      // No decision recorded => UNSET, never a silent "Y" (ISO 27001 Cl. 6.1.3(d)).
      const applicable = normalizeSoaApplicable(r.applicable);
      return {
        rowUid: typeof r.rowUid === "string" && r.rowUid ? r.rowUid : `soa-ctrl-${controlId}`,
        controlId,
        controlIdDisplay: String(r.controlIdDisplay ?? "").trim() || `A.${controlId}`,
        controlName: String(r.controlName ?? "").trim() || "-",
        applicable,
        justification: typeof r.justification === "string" ? r.justification : undefined,
        implementation: typeof r.implementation === "string" ? r.implementation : undefined,
        lastReview: typeof r.lastReview === "string" ? r.lastReview : undefined,
        notes: typeof r.notes === "string" ? r.notes : undefined,
      };
    })
    .filter(Boolean);
  return {
    templateVersion: raw.templateVersion || SOA_TEMPLATE,
    lastSyncedFromTreatmentAt: raw.lastSyncedFromTreatmentAt,
    lastAiDetailsAt: raw.lastAiDetailsAt,
    lastAiDetailsProvenance:
      raw.lastAiDetailsProvenance && typeof raw.lastAiDetailsProvenance === "object" ? raw.lastAiDetailsProvenance : undefined,
    approvalStatus:
      raw.approvalStatus === "approved" || raw.approvalStatus === "reopened_for_correction" ? raw.approvalStatus : undefined,
    approvedAt: raw.approvedAt || undefined,
    approvedBy: raw.approvedBy || undefined,
    approvedByEmail: raw.approvedByEmail || undefined,
    approvedByRole: raw.approvedByRole || undefined,
    approvalVersion: Number.isInteger(raw.approvalVersion) ? raw.approvalVersion : undefined,
    approvedContentHash: raw.approvedContentHash || undefined,
    reopenedAt: raw.reopenedAt || undefined,
    reopenReason: raw.reopenReason || undefined,
    versionHistory: Array.isArray(raw.versionHistory) ? raw.versionHistory : undefined,
    postApprovalEdits:
      raw.postApprovalEdits && typeof raw.postApprovalEdits === "object" ? raw.postApprovalEdits : undefined,
    rows,
  };
}

/** Sub-navigation order: register → treatment → SOA (keys stable for state). */
const RISK_ANALYSIS_SUB_NAV = [
  { key: "register", label: "Risk register", num: 1 },
  { key: "treatment", label: "Risk treatment plan", num: 2 },
  { key: "soa", label: "Statement of applicability", num: 3 },
];

const RISK_ANALYSIS_STATUS_STEPS = [
  { key: "register", title: "Risk identification", sub: "Identify and accept risks", icon: "file" },
  { key: "treatment", title: "Risk treatment", sub: "Actions, owners, timelines", icon: "pencil" },
  { key: "soa", title: "Statement of applicability", sub: "Scope and controls", icon: "check" },
];

/** Same SVG set as OrganizationalSetupStatusBar for visual consistency. */
function riskAnalysisStatusBarIcon(kind) {
  const common = { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" };
  if (kind === "file") {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }
  if (kind === "search") {
    return (
      <svg {...common}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    );
  }
  if (kind === "check") {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function riskAnalysisTabIndex(tabKey) {
  if (tabKey === "register") return 0;
  if (tabKey === "treatment") return 1;
  return 2;
}

function RiskAnalysisStatusBar({
  activeTab,
  treatmentUnlocked,
  soaUnlocked,
  registerFinalized,
  treatmentPublished,
  soaPhaseComplete,
}) {
  const tabIdx = riskAnalysisTabIndex(activeTab);
  let maxIdxFromUnlockedTab = tabIdx;
  if (!soaUnlocked) maxIdxFromUnlockedTab = Math.min(maxIdxFromUnlockedTab, 1);
  if (!treatmentUnlocked) maxIdxFromUnlockedTab = Math.min(maxIdxFromUnlockedTab, 0);
  /** Furthest phase finished (matches sub-tab green pills): register finalized → treatment published → SOA AI pass. */
  const completionIdx =
    !registerFinalized ? -1 : !treatmentPublished ? 0 : !soaPhaseComplete ? 1 : 2;
  const safeIdx = Math.max(maxIdxFromUnlockedTab, completionIdx);
  const parts = [];
  RISK_ANALYSIS_STATUS_STEPS.forEach((step, i) => {
    const reached = i <= safeIdx;
    parts.push(
      <div key={step.key} className="flex min-w-0 flex-1 flex-col items-center px-0.5 text-center">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-md ${
            reached ? "bg-[color:var(--color-step-reached)] text-white" : "border-2 border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]"
          }`}
        >
          {riskAnalysisStatusBarIcon(step.icon)}
        </div>
        <div
          className={`mt-2.5 text-balance text-[8px] font-black uppercase tracking-widest sm:text-[10px] ${
            reached ? "text-[color:var(--color-accent)]" : "text-[color:var(--color-text-hero)]"
          }`}
        >
          {step.title}
        </div>
        <div className="mt-0.5 text-[8px] font-medium leading-snug text-[color:var(--color-text-muted)] sm:text-[9px]">{step.sub}</div>
      </div>,
    );
    if (i < RISK_ANALYSIS_STATUS_STEPS.length - 1) {
      parts.push(
        <div
          key={`${step.key}-connector`}
          className={`mx-1 h-1 min-w-[12px] max-w-[4rem] flex-1 self-center rounded-full sm:mx-2 ${
            i < safeIdx ? "bg-[color:var(--color-step-reached)]" : "bg-[color:var(--color-bg-alt)]"
          }`}
          aria-hidden
        />,
      );
    }
  });
  return (
    <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-5 shadow-lg sm:px-6 md:px-8">
      <div className="flex w-full max-w-none items-center">{parts}</div>
    </div>
  );
}

/**
 * Workbench step 6 — Risk Analysis (advisor / lead advisor).
 */
export function RiskAnalysis({ project, user }) {
  const {
    updateProjectProfile,
    saveScopingProfile,
    fetchProjectProfile,
    fetchProjectMembers,
    riskAnalysisSubTabOverrideRef,
    fetchProjects,
    updateProjectMembers,
  } = useApp();
  const { instance, accounts } = useMsal();

  const canEdit = [
    Role.ADVISOR,
    Role.LEAD_ADVISOR,
    Role.ADMIN,
    Role.COMPANY_DELEGATE,
    Role.COMPANY_SPOC,
  ].includes(user?.role);

  const riskScale = normalizeRiskScale(project?.profile?.riskOnboarding?.riskScale);

  const riskPkg = useMemo(
    () => normalizePkg(project?.profile?.riskRegisterPackage),
    [project?.profile?.riskRegisterPackage],
  );

  const treatmentPkg = useMemo(
    () => normalizeTreatmentPkg(project?.profile?.riskTreatmentPackage),
    [project?.profile?.riskTreatmentPackage],
  );

  const soaBasePkg = useMemo(
    () => normalizeSoaPkg(project?.profile?.statementOfApplicabilityPackage),
    [project?.profile?.statementOfApplicabilityPackage],
  );

  const [rows, setRows] = useState(() => riskPkg.rows);
  const [treatmentRows, setTreatmentRows] = useState(() => treatmentPkg.rows);
  const [soaRows, setSoaRows] = useState(() => soaBasePkg.rows);
  const soaRowsRef = React.useRef(soaRows);
  const [activeTab, setActiveTab] = useState("register");
  const [generating, setGenerating] = useState(false);
  const [generatingTreatment, setGeneratingTreatment] = useState(false);
  const [publishingTreatment, setPublishingTreatment] = useState(false);
  const [syncingSoa, setSyncingSoa] = useState(false);
  const [generatingSoaDetails, setGeneratingSoaDetails] = useState(false);
  const [maxRows, setMaxRows] = useState(20);
  const [includeGaps, setIncludeGaps] = useState(true);
  const [includeIssues, setIncludeIssues] = useState(true);
  const [includePlits, setIncludePlits] = useState(true);

  const [drawerRow, setDrawerRow] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [cellDetail, setCellDetail] = useState(null);
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(RR_COL_WIDTH_STORAGE_KEY);
      if (!raw) return {};
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  });
  const [registerColumnOrder, setRegisterColumnOrder] = useState(() =>
    loadJsonColumnOrder(RR_COL_ORDER_STORAGE_KEY, REGISTER_MIDDLE_COLUMN_KEYS),
  );
  const [treatmentColumnOrder, setTreatmentColumnOrder] = useState(loadTreatmentColumnOrder);
  const [treatmentColumnWidths, setTreatmentColumnWidths] = useState(loadTreatmentWidths);

  const [registerSearchText, setRegisterSearchText] = useState("");
  const [registerFilterLifecycle, setRegisterFilterLifecycle] = useState("");
  const [registerTableResetKey, setRegisterTableResetKey] = useState(0);

  const [treatmentSearchText, setTreatmentSearchText] = useState("");
  const [treatmentTableResetKey, setTreatmentTableResetKey] = useState(0);

  React.useEffect(() => {
    setRows(riskPkg.rows);
  }, [riskPkg.rows, project?.id]);

  React.useEffect(() => {
    setTreatmentRows(treatmentPkg.rows);
  }, [treatmentPkg.rows, project?.id]);

  React.useEffect(() => {
    setSoaRows(soaBasePkg.rows);
  }, [soaBasePkg, project?.id]);

  React.useEffect(() => {
    soaRowsRef.current = soaRows;
  }, [soaRows]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!project?.id || !fetchProjectMembers) {
        setProjectMembers([]);
        return;
      }
      const mem = await fetchProjectMembers(project.id);
      const list = Array.isArray(mem) ? mem : [];
      if (!cancelled) {
        setProjectMembers(list);
        if (list.length > 0 && updateProjectMembers) updateProjectMembers(list);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.id, fetchProjectMembers, updateProjectMembers, accounts?.length]);

  React.useEffect(() => {
    try {
      localStorage.setItem(RR_COL_WIDTH_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch {
      /* private mode / storage quota */
    }
  }, [columnWidths]);

  React.useEffect(() => {
    try {
      localStorage.setItem(RR_COL_ORDER_STORAGE_KEY, JSON.stringify(registerColumnOrder));
    } catch {
      /* ignore */
    }
  }, [registerColumnOrder]);

  React.useEffect(() => {
    try {
      localStorage.setItem(RT_COL_ORDER_STORAGE_KEY, JSON.stringify(treatmentColumnOrder));
    } catch {
      /* ignore */
    }
  }, [treatmentColumnOrder]);

  React.useEffect(() => {
    try {
      localStorage.setItem(RT_COL_WIDTH_STORAGE_KEY, JSON.stringify(treatmentColumnWidths));
    } catch {
      /* ignore */
    }
  }, [treatmentColumnWidths]);

  const ownerSelectOptions = useMemo(() => {
    const fromProject = Array.isArray(project?.members) ? project.members : [];
    const merged = [...fromProject, ...projectMembers];
    const byId = new Map();
    for (const m of merged) {
      if (m && typeof m.id === "string" && m.id) {
        byId.set(m.id, m);
      }
    }
    return [...byId.values()]
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }))
      .map((m) => ({
        value: m.id,
        label: `${m.name || m.email || m.id} (${m.role || "member"})`,
      }));
  }, [projectMembers, project?.members]);

  const workflow = riskPkg.workflow || "draft";
  const isAdvisorPortal = user?.role === Role.ADVISOR;
  const isLeadPortal = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADMIN;
  const isCompanyDelegate = user?.role === Role.COMPANY_DELEGATE;
  const isCompanySpoc = user?.role === Role.COMPANY_SPOC;
  // Close/reopen is Lead Advisor only server-side (platform_admin is refused), so ADMIN is excluded here.
  const isStrictLeadAdvisor = user?.role === Role.LEAD_ADVISOR;
  const registerApproved = workflow === "finalized";
  const riskHistory = useRiskHistory(project?.id, registerApproved);

  /** Lead Advisor / Admin: full register only during LA review (or read-only elsewhere). */
  const leadAdvisorRegisterWait =
    isLeadPortal && !["in_review", "finalized"].includes(workflow);

  const canEditRiskMatrix =
    canEdit &&
    ((workflow === "draft" && isAdvisorPortal) ||
      (workflow === "with_delegate" && isCompanyDelegate) ||
      (workflow === "with_spoc" && isCompanySpoc));

  /** Generate with AI / target rows / context toggles / Accept all — advisor only, draft only. */
  const canUseRegisterBulkActions = canEdit && isAdvisorPortal && workflow === "draft";

  /** Add manual row: advisor in draft, or company roles in their review stage. */
  const canAddManualRiskRegister =
    canUseRegisterBulkActions ||
    (isCompanyDelegate && workflow === "with_delegate" && canEditRiskMatrix) ||
    (isCompanySpoc && workflow === "with_spoc" && canEditRiskMatrix);

  /** Same scope as bulk actions for the register AI endpoint from this UI. */
  const canGenerateRiskRegister = canUseRegisterBulkActions;

  const registerFinalized = workflow === "finalized";
  const treatmentPublished = Boolean(treatmentPkg?.publishedAt);

  const treatmentStepUnlocked =
    registerFinalized &&
    (isAdvisorPortal ||
      (treatmentPublished && (isLeadPortal || isCompanySpoc || isCompanyDelegate)));

  const soaStepUnlocked = registerFinalized && treatmentPublished;

  /** SOA sub-tab “done” for pill + status bar (matches last AI narrative run — see normalizeSoaPkg). */
  const soaPhaseComplete = Boolean(soaBasePkg.lastAiDetailsAt);

  const canEditSoa = Boolean(soaStepUnlocked && isAdvisorPortal);
  // Approved => locked (Commandment VII); only a reopen by the Lead Advisor re-enables edits.
  const soaApproved = isSoaApproved(soaBasePkg);
  const canEditSoaRows = canEditSoa && !soaApproved;

  const canNavigateToSoa =
    soaStepUnlocked && (isAdvisorPortal || isLeadPortal || isCompanySpoc || isCompanyDelegate);

  const rankMax = riskScale === "3" ? 3 : riskScale === "10" ? 10 : 5;

  const tableSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const onRegisterColumnDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRegisterColumnOrder((prev) => {
      const oldIdx = prev.indexOf(String(active.id));
      const newIdx = prev.indexOf(String(over.id));
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, []);

  const onTreatmentColumnDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTreatmentColumnOrder((prev) => {
      const oldIdx = prev.indexOf(String(active.id));
      const newIdx = prev.indexOf(String(over.id));
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, []);

  const registerTableComponents = useMemo(
    () => ({
      header: { cell: DraggableHeaderCell },
    }),
    [],
  );

  const treatmentTableComponents = useMemo(
    () => ({
      header: { cell: DraggableHeaderCell },
    }),
    [],
  );

  React.useEffect(() => {
    const t = riskAnalysisSubTabOverrideRef?.current;
    if (!t || !project?.id) return;
    riskAnalysisSubTabOverrideRef.current = null;
    setActiveTab(t);
  }, [project?.id, riskAnalysisSubTabOverrideRef]);

  const riskContextLine = useMemo(() => {
    const co = project?.companyName || project?.name || "";
    const cert = project?.certification || project?.frameworkType || "";
    if (co && cert) return `${co} · ${cert}`;
    return co || cert || "";
  }, [project?.companyName, project?.name, project?.certification, project?.frameworkType]);

  const persistPackage = useCallback(
    async (nextRows) => {
      if (!project?.id || !saveScopingProfile || !updateProjectProfile) return false;
      const nextPkg = {
        ...riskPkg,
        templateVersion: RR_TEMPLATE,
        rows: nextRows,
      };
      const base = project.profile || {};
      updateProjectProfile(project.id, { ...base, riskRegisterPackage: nextPkg });
      const ok = await saveScopingProfile(
        project.id,
        pickScopingPersistPayload({ riskRegisterPackage: nextPkg }),
      );
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      return ok;
    },
    [project?.id, project?.profile, riskPkg, saveScopingProfile, updateProjectProfile, fetchProjectProfile],
  );

  const onRiskOwnerChange = useCallback(
    async (rowUid, userId) => {
      const next = renumberRiskIds(
        rows.map((r) => {
          if (r.rowUid !== rowUid) return r;
          if (!userId) {
            return { ...r, riskOwnerUserId: undefined, riskOwner: r.riskOwner || "" };
          }
          const m = projectMembers.find((x) => x.id === userId);
          return { ...r, riskOwnerUserId: userId, riskOwner: m?.name || r.riskOwner || "" };
        }),
      );
      setRows(next);
      await persistPackage(next);
    },
    [rows, projectMembers, persistPackage],
  );

  const patchRowFields = useCallback(
    async (rowUid, patch) => {
      const next = renumberRiskIds(
        rows.map((r) => {
          if (r.rowUid !== rowUid) return r;
          const merged = { ...r, ...patch };
          const consequenceRank = ciaConsequenceBinarySumFromRow(merged);
          const body = { ...merged, consequenceRank };
          const sc = computeRiskRegisterScore(body, riskScale);
          return { ...body, ...sc };
        }),
      );
      setRows(next);
      await persistPackage(next);
    },
    [rows, persistPackage, riskScale],
  );

  const syncPackageFromServer = useCallback(
    (pkg) => {
      if (!pkg || !project?.id) return;
      updateProjectProfile(project.id, { ...(project.profile || {}), riskRegisterPackage: pkg });
      setRows(pkg.rows || []);
      if (fetchProjectProfile) void fetchProjectProfile(project.id);
      if (fetchProjects) void fetchProjects();
    },
    [project?.id, project?.profile, updateProjectProfile, fetchProjectProfile, fetchProjects],
  );

  const persistTreatmentPackage = useCallback(
    async (nextTreatmentRows) => {
      if (!project?.id || !saveScopingProfile || !updateProjectProfile) return false;
      const nextPkg = {
        ...treatmentPkg,
        templateVersion: RT_TEMPLATE,
        rows: nextTreatmentRows,
      };
      const base = project.profile || {};
      updateProjectProfile(project.id, { ...base, riskTreatmentPackage: nextPkg });
      const ok = await saveScopingProfile(
        project.id,
        pickScopingPersistPayload({ riskTreatmentPackage: nextPkg }),
      );
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      return ok;
    },
    [project?.id, project?.profile, treatmentPkg, saveScopingProfile, updateProjectProfile, fetchProjectProfile],
  );

  const syncTreatmentFromServer = useCallback(
    (pkg) => {
      if (!pkg || !project?.id) return;
      updateProjectProfile(project.id, { ...(project.profile || {}), riskTreatmentPackage: pkg });
      setTreatmentRows(pkg.rows || []);
      if (fetchProjectProfile) void fetchProjectProfile(project.id);
      if (fetchProjects) void fetchProjects();
    },
    [project?.id, project?.profile, updateProjectProfile, fetchProjectProfile, fetchProjects],
  );

  const syncSoaFromServer = useCallback(
    (pkg) => {
      if (!pkg || !project?.id) return;
      const norm = normalizeSoaPkg(pkg);
      updateProjectProfile(project.id, { ...(project.profile || {}), statementOfApplicabilityPackage: norm });
      setSoaRows(norm.rows || []);
      if (fetchProjectProfile) void fetchProjectProfile(project.id);
      if (fetchProjects) void fetchProjects();
    },
    [project?.id, project?.profile, updateProjectProfile, fetchProjectProfile, fetchProjects],
  );

  const persistSoaPackage = useCallback(
    async (nextRows) => {
      if (!project?.id || !saveScopingProfile || !updateProjectProfile) return false;
      // An approved SoA is locked server-side (409 SOA_APPROVED_LOCKED); never attempt the write.
      if (isSoaApproved(soaBasePkg)) return false;
      const nextPkg = { ...soaBasePkg, templateVersion: SOA_TEMPLATE, rows: nextRows };
      const base = project.profile || {};
      updateProjectProfile(project.id, { ...base, statementOfApplicabilityPackage: nextPkg });
      const ok = await saveScopingProfile(
        project.id,
        pickScopingPersistPayload({ statementOfApplicabilityPackage: stripSoaServerOwnedFields(nextPkg) }),
      );
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      return ok;
    },
    [
      project?.id,
      project?.profile,
      soaBasePkg,
      saveScopingProfile,
      updateProjectProfile,
      fetchProjectProfile,
    ],
  );

  const persistSoaRowsFromRef = useCallback(async () => {
    return persistSoaPackage(soaRowsRef.current);
  }, [persistSoaPackage]);

  const postRiskRegisterFlow = useCallback(
    async (subPath) => {
      if (!project?.id) return;
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/risk-register/${subPath}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Request failed.");
        return;
      }
      syncPackageFromServer(json.data?.riskRegisterPackage);
      if (subPath === "submit-review" || subPath === "submit-to-delegate") {
        message.success("Sent to company delegate.");
      } else if (subPath === "submit-to-spoc") {
        message.success("Sent to company SPOC.");
      } else if (subPath === "submit-to-lead-advisor") {
        message.success("Sent to Lead Advisor for review.");
      } else if (subPath === "approve") {
        message.success("Risk register approved.");
      } else if (subPath === "withdraw-review") {
        message.success("Returned the risk register to the company SPOC for changes.");
      }
    },
    [project?.id, instance, syncPackageFromServer],
  );

  const onGenerate = useCallback(async () => {
    if (!canGenerateRiskRegister || !project?.id) return;
    setGenerating(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/risk-register/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify({
          maxRows,
          includeGaps,
          includeIssues,
          includePlits,
          appendBatch: false,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not generate risk register.");
        return;
      }
      const pkg = json.data?.riskRegisterPackage;
      if (pkg) syncPackageFromServer(pkg);
      if (Array.isArray(json.meta?.warnings) && json.meta.warnings.length) {
        message.warning(json.meta.warnings.join(" "));
      }
      message.success("AI risk proposals generated.");
    } finally {
      setGenerating(false);
    }
  }, [
    canGenerateRiskRegister,
    project?.id,
    instance,
    maxRows,
    includeGaps,
    includeIssues,
    includePlits,
    syncPackageFromServer,
  ]);

  const acceptRow = useCallback(
    async (rowUid) => {
      const next = renumberRiskIds(
        rows.map((r) =>
          r.rowUid === rowUid
            ? { ...r, lifecycle: "accepted", acceptedAt: new Date().toISOString() }
            : r,
        ),
      );
      setRows(next);
      await persistPackage(next);
      message.success("Risk accepted into register.");
    },
    [rows, persistPackage],
  );

  const rejectRow = useCallback(
    async (rowUid) => {
      const row = rows.find((r) => r.rowUid === rowUid);
      const next = renumberRiskIds(
        rows.map((r) =>
          r.rowUid === rowUid
            ? { ...r, lifecycle: "rejected", rejectedAt: new Date().toISOString() }
            : r,
        ),
      );
      setRows(next);
      await persistPackage(next);
      message.success(
        row?.lifecycle === "manual" ? "Manual risk removed." : "Risk rejected.",
      );
    },
    [rows, persistPackage],
  );

  const addManual = useCallback(async () => {
    const row = emptyManualRow(riskScale);
    const next = renumberRiskIds([...rows, row]);
    const merged = next.find((r) => r.rowUid === row.rowUid);
    setRows(next);
    if (merged) {
      setDrawerRow({
        ...merged,
        confidentialityAffected: merged.confidentialityAffected ?? "N",
        integrityAffected: merged.integrityAffected ?? "N",
        availabilityAffected: merged.availabilityAffected ?? "N",
        privacyAffected: merged.privacyAffected ?? "N",
      });
    }
    const ok = await persistPackage(next);
    if (ok) {
      message.success("Manual risk added - complete the details below.");
    } else {
      message.error("Could not save the new row to the server. Edit below and choose Save, or try again.");
    }
  }, [rows, persistPackage, riskScale]);

  const saveDrawer = useCallback(async () => {
    if (!drawerRow) return;
    if (!String(drawerRow.riskTitle || "").trim()) {
      message.error("Risk title is required before saving.");
      return;
    }
    const consequenceRank = ciaConsequenceBinarySumFromRow(drawerRow);
    const body = { ...drawerRow, consequenceRank };
    const sc = computeRiskRegisterScore(body, riskScale);
    const merged = { ...body, ...sc };
    const next = renumberRiskIds(rows.map((r) => (r.rowUid === drawerRow.rowUid ? merged : r)));
    setRows(next);
    setDrawerRow(null);
    await persistPackage(next);
    message.success("Row saved.");
  }, [drawerRow, rows, persistPackage, riskScale]);

  const drawerPreviewScore = useMemo(() => {
    if (!drawerRow) return { riskScore: 0, riskSignificanceCategory: "-" };
    const consequenceRank = ciaConsequenceBinarySumFromRow(drawerRow);
    const body = { ...drawerRow, consequenceRank };
    const sc = computeRiskRegisterScore(body, riskScale);
    return {
      riskScore: sc.riskScore,
      riskSignificanceCategory: sc.riskSignificanceCategory || "-",
    };
  }, [drawerRow, riskScale]);

  const acceptAllProposed = useCallback(async () => {
    const next = renumberRiskIds(
      rows.map((r) =>
        r.lifecycle === "ai_proposed"
          ? { ...r, lifecycle: "accepted", acceptedAt: new Date().toISOString() }
          : r,
      ),
    );
    setRows(next);
    await persistPackage(next);
    message.success("All AI proposals accepted.");
  }, [rows, persistPackage]);

  const visibleRows = useMemo(() => rows.filter((r) => r.lifecycle !== "rejected"), [rows]);

  const hasPendingAiProposals = useMemo(
    () => visibleRows.some((r) => r.lifecycle === "ai_proposed"),
    [visibleRows],
  );

  const registerFacetOptions = useMemo(() => {
    const categories = new Set();
    const sources = new Set();
    const domains = new Set();
    const lifecycles = new Set();
    for (const r of visibleRows) {
      if (r.riskCategory) categories.add(String(r.riskCategory));
      if (r.riskSource) sources.add(String(r.riskSource));
      if (r.riskDomain) domains.add(String(r.riskDomain));
      if (r.lifecycle) lifecycles.add(String(r.lifecycle));
    }
    return {
      categories: [...categories].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
      sources: [...sources].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
      domains: [...domains].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
      lifecycles: [...lifecycles].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    };
  }, [visibleRows]);

  React.useEffect(() => {
    if (!treatmentStepUnlocked && activeTab === "treatment") setActiveTab("register");
    if (!soaStepUnlocked && activeTab === "soa") setActiveTab("register");
  }, [treatmentStepUnlocked, soaStepUnlocked, activeTab]);

  const registerRowsForTreatment = useMemo(
    () => rows.filter((r) => rowEligibleForTreatmentPlan(r)),
    [rows],
  );

  const canGenerateTreatment =
    registerFinalized && isAdvisorPortal && registerRowsForTreatment.length > 0;

  const canEditTreatmentPlan = treatmentStepUnlocked && isAdvisorPortal;

  const onGenerateTreatment = useCallback(async () => {
    if (!canGenerateTreatment || !project?.id) return;
    setGeneratingTreatment(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/risk-treatment/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not generate risk treatment plan.");
        return;
      }
      const pkg = json.data?.riskTreatmentPackage;
      if (pkg) syncTreatmentFromServer(pkg);
      if (Array.isArray(json.meta?.warnings) && json.meta.warnings.length) {
        message.warning(json.meta.warnings.join(" "));
      }
      message.success("Risk treatment plan generated.");
    } finally {
      setGeneratingTreatment(false);
    }
  }, [canGenerateTreatment, project?.id, instance, syncTreatmentFromServer]);

  const onPublishTreatment = useCallback(async () => {
    if (!project?.id || !isAdvisorPortal || !registerFinalized || treatmentPublished) return;
    setPublishingTreatment(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/risk-treatment/publish`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not share the treatment plan.");
        return;
      }
      const pkg = json.data?.riskTreatmentPackage;
      if (pkg) syncTreatmentFromServer(pkg);
      try {
        const syncRes = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/statement-of-applicability/sync`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
          },
        );
        const syncJson = await syncRes.json().catch(() => ({}));
        if (syncRes.ok && syncJson.success && syncJson.data?.statementOfApplicabilityPackage) {
          syncSoaFromServer(syncJson.data.statementOfApplicabilityPackage);
        } else if (syncJson.error?.message) {
          message.warning(syncJson.error.message);
        }
      } catch {
        message.warning(
          "Treatment plan was shared. Open Statement of applicability and sync from treatment if rows are empty.",
        );
      }
      message.success(
        "Treatment plan shared with the company and Lead Advisor (read-only). Statement of applicability is ready.",
      );
    } finally {
      setPublishingTreatment(false);
    }
  }, [
    project?.id,
    instance,
    isAdvisorPortal,
    registerFinalized,
    treatmentPublished,
    syncTreatmentFromServer,
    syncSoaFromServer,
  ]);

  const onSyncSoaFromTreatment = useCallback(async () => {
    if (!project?.id || !canEditSoa) return;
    setSyncingSoa(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/statement-of-applicability/sync`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        const parsed = parseSoaApprovalError(res.status, json);
        message.error(plainSoaGateMessage(parsed.message).message || "Could not sync the Statement of applicability.");
        if (isSoaLockedError(parsed) && fetchProjectProfile) void fetchProjectProfile(project.id);
        return;
      }
      syncSoaFromServer(json.data?.statementOfApplicabilityPackage);
      message.success("Control list refreshed from the published treatment plan.");
    } finally {
      setSyncingSoa(false);
    }
  }, [project?.id, canEditSoa, instance, syncSoaFromServer, fetchProjectProfile]);

  const soaApiPost = useCallback(
    async (path, body) => {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/statement-of-applicability/${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const json = await res.json().catch(() => ({}));
      return { res, json };
    },
    [project?.id, instance],
  );

  // ISO/IEC 27001:2022 Cl. 6.1.3(d); Workflow slide 10 - Lead Advisor approves the SoA (Commandment IV).
  const onApproveSoa = useCallback(async () => {
    if (!project?.id || !isStrictLeadAdvisor) return { ok: false, message: "Only the Lead Advisor can approve." };
    try {
      const { res, json } = await soaApiPost("approve");
      if (!res.ok || !json.success) {
        const parsed = parseSoaApprovalError(res.status, json);
        if (isSoaLockedError(parsed) || /already approved/i.test(parsed.message)) {
          if (fetchProjectProfile) void fetchProjectProfile(project.id);
        }
        return { ok: false, message: parsed.message, blockers: parsed.blockers };
      }
      syncSoaFromServer(json.data?.statementOfApplicabilityPackage);
      message.success("Statement of Applicability approved and locked.");
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not reach the server. Please try again." };
    }
  }, [project?.id, isStrictLeadAdvisor, soaApiPost, syncSoaFromServer, fetchProjectProfile]);

  const onReopenSoa = useCallback(
    async (reason) => {
      if (!project?.id || !isStrictLeadAdvisor) return { ok: false, message: "Only the Lead Advisor can reopen." };
      try {
        const { res, json } = await soaApiPost("reopen", { reason });
        if (!res.ok || !json.success) {
          const parsed = parseSoaApprovalError(res.status, json);
          return { ok: false, message: parsed.message };
        }
        syncSoaFromServer(json.data?.statementOfApplicabilityPackage);
        message.success("Statement of Applicability reopened for correction. The approved version is kept in the history.");
        return { ok: true };
      } catch {
        return { ok: false, message: "Could not reach the server. Please try again." };
      }
    },
    [project?.id, isStrictLeadAdvisor, soaApiPost, syncSoaFromServer],
  );

  const onGenerateSoaDetails = useCallback(async () => {
    if (!project?.id || !canEditSoa) return;
    setGeneratingSoaDetails(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/statement-of-applicability/generate-details`,
        { method: "POST", headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        const parsed = parseSoaApprovalError(res.status, json);
        message.error(plainSoaGateMessage(parsed.message).message || "Could not generate SOA details.");
        if (isSoaLockedError(parsed) && fetchProjectProfile) void fetchProjectProfile(project.id);
        return;
      }
      syncSoaFromServer(json.data?.statementOfApplicabilityPackage);
      if (Array.isArray(json.meta?.warnings) && json.meta.warnings.length) {
        message.warning(json.meta.warnings.join(" "));
      }
      message.success("Justification, implementation, and last review generated.");
    } finally {
      setGeneratingSoaDetails(false);
    }
  }, [project?.id, canEditSoa, instance, syncSoaFromServer, fetchProjectProfile]);

  const patchTreatmentRow = useCallback(
    async (rowUid, patch) => {
      const existing = treatmentRows.find((t) => t.rowUid === rowUid);
      if (!existing) {
        message.warning("Generate the treatment plan first.");
        return;
      }
      let nextPatch = { ...patch };
      if (nextPatch.recurrent === "N") nextPatch = { ...nextPatch, frequency: undefined };
      const next = treatmentRows.map((t) => (t.rowUid === rowUid ? { ...t, ...nextPatch } : t));
      setTreatmentRows(next);
      await persistTreatmentPackage(next);
    },
    [treatmentRows, persistTreatmentPackage],
  );

  const treatmentTableData = useMemo(() => {
    const byUid = new Map(treatmentRows.map((t) => [t.rowUid, t]));
    return registerRowsForTreatment.map((r) => ({
      rowUid: r.rowUid,
      register: r,
      treatment: byUid.get(r.rowUid),
    }));
  }, [registerRowsForTreatment, treatmentRows]);

  const deferredTreatmentSearch = React.useDeferredValue(treatmentSearchText.trim().toLowerCase());

  const filteredTreatmentRows = useMemo(() => {
    let list = treatmentTableData;
    if (deferredTreatmentSearch) {
      const q = deferredTreatmentSearch;
      list = list.filter((row) => {
        const reg = row.register;
        const ctrl = (row.treatment?.controls || []).join(" ").toLowerCase();
        const ownerDisp = registerOwnerDisplayName(reg, projectMembers).toLowerCase();
        const blob = [
          reg.riskId,
          reg.riskTitle,
          reg.riskDescription,
          reg.riskCategory,
          reg.riskSource,
          reg.riskDomain,
          reg.department,
          reg.riskOwner,
          ownerDisp,
          row.treatment?.treatmentAsset,
          ctrl,
        ]
          .map((x) => String(x ?? "").toLowerCase())
          .join("\n");
        return blob.includes(q);
      });
    }
    return list;
  }, [treatmentTableData, deferredTreatmentSearch, projectMembers]);

  const treatmentColumns = useMemo(() => {
    const w = (key, def) => treatmentColumnWidths[key] ?? def;
    const inherentScore = (row) =>
      computeRiskRegisterScore(
        { ...row.register, consequenceRank: ciaConsequenceBinarySumFromRow(row.register) },
        riskScale,
      ).riskScore;
    const rankNum = (v) => (typeof v === "number" ? v : -1);
    const categoryVals = [
      ...new Set(treatmentTableData.map((x) => x.register.riskCategory).filter(Boolean)),
    ].sort((a, b) => cmpLocaleStr(a, b));
    const categoryFilters = categoryVals.map((v) => ({ text: v, value: v }));
    if (treatmentTableData.some((row) => !String(row.register.riskCategory ?? "").trim())) {
      categoryFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }
    const strategyFilters = TREATMENT_STRATEGY_OPTIONS.map((o) => ({ text: o.label, value: o.value }));
    if (treatmentTableData.some((row) => !String(row.treatment?.strategy ?? "").trim())) {
      strategyFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }
    const acceptableFilters = [
      { text: "Y", value: "Y" },
      { text: "N", value: "N" },
    ];
    if (treatmentTableData.some((row) => !String(row.treatment?.acceptable ?? "").trim())) {
      acceptableFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }
    const middleByKey = {
      tr_title: {
        title: "Title",
        key: "tr_title",
        width: w("tr_title", 180),
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.register.riskTitle, b.register.riskTitle),
        render: (_, row) => row.register.riskTitle || "-",
      },
      tr_owner: {
        title: "Owner",
        key: "tr_owner",
        width: w("tr_owner", 200),
        ellipsis: { showTitle: true },
        sorter: (a, b) =>
          cmpLocaleStr(
            registerOwnerDisplayName(a.register, projectMembers),
            registerOwnerDisplayName(b.register, projectMembers),
          ),
        render: (_, row) => registerOwnerDisplayName(row.register, projectMembers),
      },
      tr_category: {
        title: "Category",
        key: "tr_category",
        width: w("tr_category", 110),
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.register.riskCategory, b.register.riskCategory),
        filters: categoryFilters,
        filterMultiple: true,
        filterSearch: true,
        onFilter: (value, row) => {
          const c = String(row.register.riskCategory ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !c.trim();
          return c === value;
        },
        render: (_, row) => row.register.riskCategory || "-",
      },
      tr_score: {
        title: "Score",
        key: "tr_score",
        width: w("tr_score", 72),
        sorter: (a, b) => inherentScore(a) - inherentScore(b),
        render: (_, row) => {
          const sc = computeRiskRegisterScore(
            { ...row.register, consequenceRank: ciaConsequenceBinarySumFromRow(row.register) },
            riskScale,
          );
          return sc.riskScore;
        },
      },
      tr_asset: {
        title: "Asset",
        key: "tr_asset",
        width: w("tr_asset", 140),
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.treatment?.treatmentAsset, b.treatment?.treatmentAsset),
        render: (_, row) => row.treatment?.treatmentAsset || "-",
      },
      tr_strategy: {
        title: "Strategy",
        key: "tr_strategy",
        width: w("tr_strategy", 130),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.strategy, b.treatment?.strategy),
        filters: strategyFilters,
        filterMultiple: true,
        filterSearch: true,
        onFilter: (value, row) => {
          const s = String(row.treatment?.strategy ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !s;
          return s === value;
        },
        render: (_, row) => (
          <Select
            className="w-full min-w-[110px]"
            size="small"
            disabled={!canEditTreatmentPlan || !row.treatment}
            value={row.treatment?.strategy}
            placeholder="-"
            options={TREATMENT_STRATEGY_OPTIONS}
            onChange={(v) => void patchTreatmentRow(row.rowUid, { strategy: v })}
          />
        ),
      },
      tr_controls: {
        title: "Controls",
        key: "tr_controls",
        width: w("tr_controls", 220),
        sorter: (a, b) =>
          cmpLocaleStr(
            (a.treatment?.controls || []).join("\n"),
            (b.treatment?.controls || []).join("\n"),
          ),
        render: (_, row) => {
          const c = row.treatment?.controls;
          const maxW = treatmentColumnWidths.tr_controls ?? 220;
          if (!c?.length) return "-";
          const visible = c.slice(0, TREATMENT_CONTROLS_VISIBLE_CHIPS);
          const rest = c.slice(TREATMENT_CONTROLS_VISIBLE_CHIPS);
          return (
            <Space size={[4, 4]} wrap style={{ maxWidth: maxW }}>
              {visible.map((x, i) => (
                <TreatmentControlTag key={i} fullText={x} indexZeroBased={i} />
              ))}
              {rest.length ? (
                <Popover
                  title={`More controls (${rest.length})`}
                  trigger="click"
                  content={
                    <ul className="m-0 max-h-[min(50vh,320px)] max-w-md list-disc space-y-2 overflow-y-auto pl-4 text-sm text-[color:var(--color-text-hero)]">
                      {rest.map((x, j) => {
                        const idx = TREATMENT_CONTROLS_VISIBLE_CHIPS + j;
                        const raw = String(x ?? "").trim();
                        const p = parseAnnexControlRef(raw);
                        const numLabel =
                          p.number != null && p.number !== ""
                            ? p.number
                            : treatmentControlShortLabel(raw, idx);
                        const body = p.name != null && p.name !== "" ? p.name : raw;
                        return (
                          <li key={j}>
                            <span className="font-semibold text-[color:var(--color-text-muted-dark)]">{numLabel}: </span>
                            {body}
                          </li>
                        );
                      })}
                    </ul>
                  }
                >
                  <Tag className="m-0 cursor-pointer text-xs hover:border-[color:var(--color-accent)]">+{rest.length}</Tag>
                </Popover>
              ) : null}
            </Space>
          );
        },
      },
      tr_revL: {
        title: "Rev. L",
        key: "tr_revL",
        width: w("tr_revL", 56),
        sorter: (a, b) =>
          rankNum(a.treatment?.revisedLikelihoodRank) - rankNum(b.treatment?.revisedLikelihoodRank),
        render: (_, row) => row.treatment?.revisedLikelihoodRank ?? "-",
      },
      tr_revC: {
        title: "Rev. C",
        key: "tr_revC",
        width: w("tr_revC", 56),
        sorter: (a, b) =>
          rankNum(a.treatment?.revisedConsequenceRank) - rankNum(b.treatment?.revisedConsequenceRank),
        render: (_, row) => row.treatment?.revisedConsequenceRank ?? "-",
      },
      tr_revS: {
        title: "Rev. S",
        key: "tr_revS",
        width: w("tr_revS", 56),
        sorter: (a, b) => rankNum(a.treatment?.revisedSeverityRank) - rankNum(b.treatment?.revisedSeverityRank),
        render: (_, row) => row.treatment?.revisedSeverityRank ?? "-",
      },
      tr_revScore: {
        title: "Rev. score",
        key: "tr_revScore",
        width: w("tr_revScore", 80),
        sorter: (a, b) => rankNum(a.treatment?.revisedRiskScore) - rankNum(b.treatment?.revisedRiskScore),
        render: (_, row) => row.treatment?.revisedRiskScore ?? "-",
      },
      tr_revSig: {
        title: "Rev. significance",
        key: "tr_revSig",
        width: w("tr_revSig", 130),
        ellipsis: true,
        sorter: (a, b) =>
          cmpLocaleStr(a.treatment?.revisedRiskSignificanceCategory, b.treatment?.revisedRiskSignificanceCategory),
        render: (_, row) => row.treatment?.revisedRiskSignificanceCategory ?? "-",
      },
      tr_acceptable: {
        title: "Acceptable",
        key: "tr_acceptable",
        width: w("tr_acceptable", 100),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.acceptable, b.treatment?.acceptable),
        filters: acceptableFilters,
        filterMultiple: true,
        onFilter: (value, row) => {
          const x = String(row.treatment?.acceptable ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !x;
          return x === value;
        },
        render: (_, row) => (
          <Select
            className="w-full min-w-[72px]"
            size="small"
            disabled={!canEditTreatmentPlan || !row.treatment}
            value={row.treatment?.acceptable}
            placeholder="-"
            options={[
              { value: "Y", label: "Y" },
              { value: "N", label: "N" },
            ]}
            onChange={(v) => void patchTreatmentRow(row.rowUid, { acceptable: v })}
          />
        ),
      },
      tr_start: {
        title: "Start",
        key: "tr_start",
        width: w("tr_start", 132),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.startDate, b.treatment?.startDate),
        render: (_, row) => (
          <Input
            type="date"
            size="small"
            className="w-[128px]"
            disabled={!canEditTreatmentPlan || !row.treatment}
            value={row.treatment?.startDate || ""}
            onChange={(e) => void patchTreatmentRow(row.rowUid, { startDate: e.target.value || undefined })}
          />
        ),
      },
      tr_end: {
        title: "End",
        key: "tr_end",
        width: w("tr_end", 132),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.endDate, b.treatment?.endDate),
        render: (_, row) => (
          <Input
            type="date"
            size="small"
            className="w-[128px]"
            disabled={!canEditTreatmentPlan || !row.treatment}
            value={row.treatment?.endDate || ""}
            onChange={(e) => void patchTreatmentRow(row.rowUid, { endDate: e.target.value || undefined })}
          />
        ),
      },
      tr_recurrent: {
        title: "Recurrent",
        key: "tr_recurrent",
        width: w("tr_recurrent", 100),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.recurrent, b.treatment?.recurrent),
        render: (_, row) => (
          <Select
            className="w-full min-w-[72px]"
            size="small"
            disabled={!canEditTreatmentPlan || !row.treatment}
            value={row.treatment?.recurrent}
            placeholder="-"
            options={[
              { value: "Y", label: "Y" },
              { value: "N", label: "N" },
            ]}
            onChange={(v) => void patchTreatmentRow(row.rowUid, { recurrent: v })}
          />
        ),
      },
      tr_freq: {
        title: "Freq",
        key: "tr_freq",
        width: w("tr_freq", 120),
        sorter: (a, b) => cmpLocaleStr(a.treatment?.frequency, b.treatment?.frequency),
        render: (_, row) => (
          <Select
            className="w-full min-w-[100px]"
            size="small"
            allowClear
            disabled={!canEditTreatmentPlan || !row.treatment || row.treatment.recurrent !== "Y"}
            value={row.treatment?.frequency}
            placeholder="-"
            options={TREATMENT_FREQ_OPTIONS}
            onChange={(v) => void patchTreatmentRow(row.rowUid, { frequency: v || undefined })}
          />
        ),
      },
    };

    const leftCol = {
      title: "Risk ID",
      key: TREATMENT_PINNED_LEFT_KEY,
      width: w(TREATMENT_PINNED_LEFT_KEY, 88),
      fixed: "left",
      sorter: (a, b) => cmpLocaleStr(a.register.riskId, b.register.riskId),
      render: (_, row) => row.register.riskId || "-",
    };

    const orderedMiddle = treatmentColumnOrder.map((k) => middleByKey[k]).filter(Boolean);

    const apply = (col) => {
      const key = col.key;
      const width = treatmentColumnWidths[key] ?? col.width;
      const pinned = key === TREATMENT_PINNED_LEFT_KEY;
      return {
        ...col,
        width,
        onHeaderCell: () => ({
          ...(pinned ? {} : { dndId: key }),
          width,
          minWidth: 48,
          onResize: (nw) => setTreatmentColumnWidths((prev) => ({ ...prev, [key]: nw })),
        }),
      };
    };

    return [apply(leftCol), ...orderedMiddle.map(apply)];
  }, [
    treatmentTableData,
    riskScale,
    canEditTreatmentPlan,
    patchTreatmentRow,
    treatmentColumnOrder,
    treatmentColumnWidths,
    projectMembers,
  ]);

  const treatmentTableScrollX = useMemo(
    () => treatmentColumns.reduce((acc, col) => acc + (typeof col.width === "number" ? col.width : 120), 0),
    [treatmentColumns],
  );

  const deferredRegisterSearch = React.useDeferredValue(registerSearchText.trim().toLowerCase());

  const filteredRegisterRows = useMemo(() => {
    let list = visibleRows;
    if (registerFilterLifecycle) list = list.filter((r) => String(r.lifecycle ?? "") === registerFilterLifecycle);
    if (deferredRegisterSearch) {
      const q = deferredRegisterSearch;
      list = list.filter((r) => {
        const blob = [
          r.riskId,
          r.riskTitle,
          r.riskDescription,
          r.riskCategory,
          r.riskSource,
          r.riskDomain,
          r.department,
        ]
          .map((x) => String(x ?? "").toLowerCase())
          .join("\n");
        return blob.includes(q);
      });
    }
    return list;
  }, [visibleRows, registerFilterLifecycle, deferredRegisterSearch]);

  const columns = useMemo(() => {
    const scored = (record) =>
      computeRiskRegisterScore(
        { ...record, consequenceRank: ciaConsequenceBinarySumFromRow(record) },
        riskScale,
      );

    const catFilters = registerFacetOptions.categories.map((v) => ({ text: v, value: v }));
    if (visibleRows.some((r) => !String(r.riskCategory ?? "").trim())) {
      catFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }
    const srcFilters = registerFacetOptions.sources.map((v) => ({ text: v, value: v }));
    if (visibleRows.some((r) => !String(r.riskSource ?? "").trim())) {
      srcFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }
    const domFilters = registerFacetOptions.domains.map((v) => ({ text: v, value: v }));
    if (visibleRows.some((r) => !String(r.riskDomain ?? "").trim())) {
      domFilters.push({ text: "(Empty)", value: REGISTER_FILTER_EMPTY });
    }

    const cellOpen = (title, getText) => ({
      onCell: (record) => ({
        onClick: () => {
          const raw = getText(record);
          setCellDetail({
            title,
            text: raw === "" || raw == null ? "-" : String(raw),
          });
        },
        style: { cursor: "pointer" },
      }),
    });

    const base = [
      {
        title: "Risk ID",
        dataIndex: "riskId",
        width: 88,
        fixed: "left",
        sorter: (a, b) => cmpLocaleStr(a.riskId, b.riskId),
        render: (v) => v || "-",
        ...cellOpen("Risk ID", (r) => r.riskId || "-"),
      },
      {
        title: "Title",
        dataIndex: "riskTitle",
        width: 200,
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.riskTitle, b.riskTitle),
        ...cellOpen("Title", (r) => r.riskTitle || "-"),
      },
      {
        title: "Department",
        dataIndex: "department",
        width: 120,
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.department, b.department),
        ...cellOpen("Department", (r) => r.department || "-"),
      },
      {
        title: "Owner",
        key: "riskOwnerUserId",
        width: 220,
        sorter: (a, b) =>
          cmpLocaleStr(
            registerOwnerDisplayName(a, projectMembers),
            registerOwnerDisplayName(b, projectMembers),
          ),
        render: (_, record) => (
          <Select
            className="w-full max-w-[220px]"
            showSearch
            optionFilterProp="label"
            allowClear
            disabled={!canEditRiskMatrix || ownerSelectOptions.length === 0}
            placeholder={
              ownerSelectOptions.length === 0
                ? "No project members"
                : record.riskOwner && !record.riskOwnerUserId
                  ? record.riskOwner
                  : OWNER_NOT_ASSIGNED
            }
            value={record.riskOwnerUserId || undefined}
            options={ownerSelectOptions}
            onChange={(v) => void onRiskOwnerChange(record.rowUid, v ?? "")}
          />
        ),
      },
      {
        title: "Category",
        dataIndex: "riskCategory",
        width: 110,
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.riskCategory, b.riskCategory),
        filters: catFilters,
        filterMultiple: true,
        filterSearch: true,
        onFilter: (value, record) => {
          const c = String(record.riskCategory ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !c.trim();
          return c === value;
        },
        ...cellOpen("Category", (r) => r.riskCategory || "-"),
      },
      {
        title: "Source",
        dataIndex: "riskSource",
        width: 120,
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.riskSource, b.riskSource),
        filters: srcFilters,
        filterMultiple: true,
        filterSearch: true,
        onFilter: (value, record) => {
          const c = String(record.riskSource ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !c.trim();
          return c === value;
        },
        ...cellOpen("Source", (r) => r.riskSource || "-"),
      },
      {
        title: "Domain",
        dataIndex: "riskDomain",
        width: 100,
        ellipsis: true,
        sorter: (a, b) => cmpLocaleStr(a.riskDomain, b.riskDomain),
        filters: domFilters,
        filterMultiple: true,
        filterSearch: true,
        onFilter: (value, record) => {
          const c = String(record.riskDomain ?? "");
          if (value === REGISTER_FILTER_EMPTY) return !c.trim();
          return c === value;
        },
        ...cellOpen("Domain", (r) => r.riskDomain || "-"),
      },
      {
        title: "Sev",
        dataIndex: "severityRank",
        width: 52,
        sorter: (a, b) => (Number(a.severityRank) || 0) - (Number(b.severityRank) || 0),
        ...(canEditRiskMatrix
          ? {}
          : cellOpen("Severity", (r) => formatSeverityRankTooltip(riskScale, r.severityRank, r.severityBands))),
        render: (v, record) => {
          const tip = (
            <pre className="m-0 whitespace-pre-wrap font-sans text-xs">
              {formatSeverityRankTooltip(riskScale, v, record.severityBands)}
            </pre>
          );
          if (canEditRiskMatrix) {
            return (
              <Tooltip title={tip}>
                <InputNumber
                  size="small"
                  className="w-14"
                  min={1}
                  max={rankMax}
                  value={v}
                  onChange={(n) => n != null && void patchRowFields(record.rowUid, { severityRank: n })}
                />
              </Tooltip>
            );
          }
          return (
            <Tooltip title={tip}>
              <span className="cursor-help border-b border-dotted border-[color:var(--color-border-strong)]">{v}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Like",
        dataIndex: "likelihoodRank",
        width: 52,
        sorter: (a, b) => (Number(a.likelihoodRank) || 0) - (Number(b.likelihoodRank) || 0),
        ...(canEditRiskMatrix
          ? {}
          : cellOpen("Likelihood", (r) =>
              formatLikelihoodRankTooltip(riskScale, r.likelihoodRank, r.likelihoodBand),
            )),
        render: (v, record) => {
          const tip = (
            <pre className="m-0 whitespace-pre-wrap font-sans text-xs">
              {formatLikelihoodRankTooltip(riskScale, v, record.likelihoodBand)}
            </pre>
          );
          if (canEditRiskMatrix) {
            return (
              <Tooltip title={tip}>
                <InputNumber
                  size="small"
                  className="w-14"
                  min={1}
                  max={rankMax}
                  value={v}
                  onChange={(n) => n != null && void patchRowFields(record.rowUid, { likelihoodRank: n })}
                />
              </Tooltip>
            );
          }
          return (
            <Tooltip title={tip}>
              <span className="cursor-help border-b border-dotted border-[color:var(--color-border-strong)]">{v}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Cons",
        dataIndex: "consequenceRank",
        width: 52,
        sorter: (a, b) =>
          ciaConsequenceBinarySumFromRow(a) - ciaConsequenceBinarySumFromRow(b),
        ...(canEditRiskMatrix
          ? {}
          : cellOpen("Consequence", (r) =>
              formatConsequenceRankTooltip(r, ciaConsequenceBinarySumFromRow(r)),
            )),
        render: (_, record) => {
          const c = ciaConsequenceBinarySumFromRow(record);
          const tip = (
            <pre className="m-0 whitespace-pre-wrap font-sans text-xs">{formatConsequenceRankTooltip(record, c)}</pre>
          );
          const tally = (
            <span
              className={
                canEditRiskMatrix
                  ? "cursor-pointer border-b border-dotted border-[color:var(--color-border-strong)]"
                  : "cursor-help border-b border-dotted border-[color:var(--color-border-strong)]"
              }
            >
              {c}
            </span>
          );
          const tallyWithTip = <Tooltip title={tip}>{tally}</Tooltip>;
          if (!canEditRiskMatrix) return tallyWithTip;
          return (
            <Popover
              title="CIA+P (Y/N per dimension)"
              trigger="click"
              content={
                <div className="flex min-w-[220px] flex-col gap-2">
                  {[
                    ["confidentialityAffected", "Confidentiality"],
                    ["integrityAffected", "Integrity"],
                    ["availabilityAffected", "Availability"],
                    ["privacyAffected", "Privacy"],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center justify-between gap-2 text-xs font-medium text-[color:var(--color-text-hero)]">
                      <span>{label}</span>
                      <Select
                        className="w-24"
                        size="small"
                        value={record[key] ?? "N"}
                        options={[
                          { value: "N", label: "N" },
                          { value: "Y", label: "Y" },
                        ]}
                        onChange={(v) => void patchRowFields(record.rowUid, { [key]: v })}
                      />
                    </label>
                  ))}
                </div>
              }
            >
              {tallyWithTip}
            </Popover>
          );
        },
      },
      {
        title: "Score",
        dataIndex: "riskScore",
        width: 72,
        sorter: (a, b) => scored(a).riskScore - scored(b).riskScore,
        ...cellOpen("Score", (r) => String(scored(r).riskScore)),
        render: (_, record) => {
          const sc = scored(record);
          return (
            <Tag color={riskSignificanceTagColor(sc.riskSignificanceCategory)} className="m-0 font-bold">
              {sc.riskScore}
            </Tag>
          );
        },
      },
      {
        title: "Significance",
        dataIndex: "riskSignificanceCategory",
        width: 200,
        ellipsis: true,
        sorter: (a, b) =>
          cmpLocaleStr(scored(a).riskSignificanceCategory, scored(b).riskSignificanceCategory),
        ...cellOpen("Significance", (r) => scored(r).riskSignificanceCategory),
        render: (_, record) => {
          const cat = scored(record).riskSignificanceCategory;
          if (!cat) return "-";
          return <Tag color={riskSignificanceTagColor(cat)}>{cat}</Tag>;
        },
      },
      {
        title: "Actions",
        key: "act",
        fixed: "right",
        width: registerApproved ? 360 : 220,
        render: (_, record) => (
          <Space size="small" wrap>
            {registerApproved ? <RiskOpenClosedBadge history={riskHistory} rowUid={record.rowUid} /> : null}
            {registerApproved ? (
              <RiskRowLifecycleActions
                projectId={project?.id}
                record={record}
                history={riskHistory}
                isLeadAdvisor={isStrictLeadAdvisor}
                registerApproved={registerApproved}
              />
            ) : null}
            <Button
              type="link"
              size="small"
              disabled={!canEditRiskMatrix}
              onClick={() =>
                setDrawerRow({
                  ...record,
                  confidentialityAffected: record.confidentialityAffected ?? "N",
                  integrityAffected: record.integrityAffected ?? "N",
                  availabilityAffected: record.availabilityAffected ?? "N",
                  privacyAffected: record.privacyAffected ?? "N",
                })
              }
            >
              Edit
            </Button>
            {record.lifecycle === "ai_proposed" && canEditRiskMatrix ? (
              <>
                <Button type="link" size="small" onClick={() => acceptRow(record.rowUid)}>
                  Accept
                </Button>
                <Button type="link" size="small" danger onClick={() => rejectRow(record.rowUid)}>
                  Reject
                </Button>
              </>
            ) : null}
            {record.lifecycle === "manual" && canEditRiskMatrix ? (
              <Button type="link" size="small" danger onClick={() => rejectRow(record.rowUid)}>
                Reject
              </Button>
            ) : null}
          </Space>
        ),
      },
    ];

    const byKey = new Map(base.map((col) => [riskRegisterColumnKey(col), col]));
    const left = byKey.get(REGISTER_PINNED_LEFT_KEY);
    const right = byKey.get(REGISTER_PINNED_RIGHT_KEY);
    const middleOrdered = registerColumnOrder.map((key) => byKey.get(key)).filter(Boolean);
    const merged = [left, ...middleOrdered, right].filter(Boolean);

    return merged.map((col) => {
      const k = riskRegisterColumnKey(col);
      const w = columnWidths[k] ?? col.width;
      const pinned = k === REGISTER_PINNED_LEFT_KEY || k === REGISTER_PINNED_RIGHT_KEY;
      return {
        ...col,
        width: w,
        onHeaderCell: () => ({
          ...(pinned ? {} : { dndId: k }),
          width: w,
          minWidth: 48,
          onResize: (nw) => setColumnWidths((prev) => ({ ...prev, [k]: nw })),
        }),
      };
    });
  }, [
    registerColumnOrder,
    acceptRow,
    rejectRow,
    riskScale,
    canEditRiskMatrix,
    ownerSelectOptions,
    onRiskOwnerChange,
    columnWidths,
    patchRowFields,
    rankMax,
    registerFacetOptions,
    visibleRows,
    projectMembers,
    registerApproved,
    riskHistory,
    isStrictLeadAdvisor,
    project?.id,
  ]);

  const registerTableScrollX = useMemo(
    () => columns.reduce((acc, col) => acc + (typeof col.width === "number" ? col.width : 120), 0),
    [columns],
  );

  const registerPanel = (
    <div className="space-y-6">
      {leadAdvisorRegisterWait ? (
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-10 text-center">
          <h3 className="text-lg font-black text-[color:var(--color-text-hero)]">Risk register</h3>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted-dark)]">
            The risk register is with the company delegate or SPOC. It will appear here for Lead Advisor review after
            the company forwards it.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5">
            {canUseRegisterBulkActions ? (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Target rows</div>
                  <InputNumber
                    min={3}
                    max={60}
                    value={maxRows}
                    onChange={(v) => setMaxRows(v ?? 20)}
                    disabled={!canUseRegisterBulkActions}
                  />
                </div>
                <Checkbox
                  checked={includeGaps}
                  onChange={(e) => setIncludeGaps(e.target.checked)}
                  disabled={!canUseRegisterBulkActions}
                >
                  Gap analysis context
                </Checkbox>
                <Checkbox
                  checked={includeIssues}
                  onChange={(e) => setIncludeIssues(e.target.checked)}
                  disabled={!canUseRegisterBulkActions}
                >
                  Onboarding issues
                </Checkbox>
                <Checkbox
                  checked={includePlits}
                  onChange={(e) => setIncludePlits(e.target.checked)}
                  disabled={!canUseRegisterBulkActions}
                >
                  Master data
                </Checkbox>
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-3">
              {canUseRegisterBulkActions ? (
                <>
                  <Button
                    type="primary"
                    loading={generating}
                    disabled={!canGenerateRiskRegister}
                    onClick={() => void onGenerate()}
                  >
                    Generate with AI
                  </Button>
                  <Button disabled={!canAddManualRiskRegister} onClick={() => void addManual()}>
                    Add manual risk
                  </Button>
                  <Button
                    disabled={
                      !canUseRegisterBulkActions || !visibleRows.some((r) => r.lifecycle === "ai_proposed")
                    }
                    onClick={() => void acceptAllProposed()}
                  >
                    Accept all proposals
                  </Button>
                </>
              ) : null}
              {!canUseRegisterBulkActions && canAddManualRiskRegister ? (
                <Button type="primary" onClick={() => void addManual()}>
                  Add manual risk
                </Button>
              ) : null}
              {isAdvisorPortal ? (
                <Button
                  type="primary"
                  ghost
                  disabled={
                    workflow !== "draft" || visibleRows.length === 0 || hasPendingAiProposals
                  }
                  onClick={() => void postRiskRegisterFlow("submit-to-delegate")}
                >
                  Send to company delegate
                </Button>
              ) : null}
              {isCompanyDelegate && workflow === "with_delegate" ? (
                <Button
                  type="primary"
                  ghost
                  disabled={visibleRows.length === 0 || hasPendingAiProposals}
                  onClick={() => void postRiskRegisterFlow("submit-to-spoc")}
                >
                  Send to company SPOC
                </Button>
              ) : null}
              {isCompanySpoc && workflow === "with_spoc" ? (
                <Button
                  type="primary"
                  ghost
                  disabled={visibleRows.length === 0 || hasPendingAiProposals}
                  onClick={() => void postRiskRegisterFlow("submit-to-lead-advisor")}
                >
                  Send to Lead Advisor for review
                </Button>
              ) : null}
              {isLeadPortal && workflow === "in_review" ? (
                <>
                  <Tooltip title="Returns the register to the company SPOC stage so they can make changes.">
                    <span className="inline-block">
                      <Button onClick={() => void postRiskRegisterFlow("withdraw-review")}>
                        Return to company SPOC
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      hasPendingAiProposals
                        ? "Every AI proposal must be accepted or rejected before you can approve the register."
                        : undefined
                    }
                  >
                    <span className="inline-block">
                      {/* Approve is Lead Advisor only server-side (PLATFORM_ADMIN_APPROVAL_DENIED); admin keeps read access. */}
                      {canApproveRiskRegister(user?.role) ? (
                        <Button
                          type="primary"
                          disabled={hasPendingAiProposals}
                          onClick={() => void postRiskRegisterFlow("approve")}
                        >
                          Approve risk register
                        </Button>
                      ) : null}
                    </span>
                  </Tooltip>
                </>
              ) : null}
            </div>
            {isAdvisorPortal && workflow === "draft" && hasPendingAiProposals ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-text-hero)]">
                Accept or reject every AI proposal before you can send the register to the company delegate.
              </Text>
            ) : null}
            {workflow === "in_review" ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-warn-fg)]">
                This register is under Lead Advisor review. Bulk actions (generate, add manual, accept all) are
                disabled.
                {isAdvisorPortal
                  ? " You can edit again only if the Lead Advisor returns the register to the company."
                  : null}
              </Text>
            ) : null}
            {workflow === "with_delegate" && isAdvisorPortal ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-text-hero)]">
                The register is with the company delegate for review. You have read-only access until they forward it.
              </Text>
            ) : null}
            {workflow === "with_spoc" && isAdvisorPortal ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-text-hero)]">
                The register is with the company SPOC for review. You have read-only access until they forward it.
              </Text>
            ) : null}
            {workflow === "with_delegate" && isCompanyDelegate ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-text-hero)]">
                Review the risk register and add manual rows as needed, then send it to the company SPOC.
              </Text>
            ) : null}
            {workflow === "with_spoc" && isCompanySpoc ? (
              <Text className="mt-3 block text-xs font-semibold text-[color:var(--color-text-hero)]">
                Review the risk register and add manual rows as needed, then send it to the Lead Advisor.
              </Text>
            ) : null}
            {workflow === "finalized" ? (
              <div className="mt-3 space-y-3">
                <Text className="block text-xs font-semibold text-[color:var(--color-ok-fg)]">
                  Lead Advisor approved. The risk register is read-only. Continue in Risk treatment plan when ready.
                </Text>
                {treatmentStepUnlocked && (isAdvisorPortal || (isLeadPortal && treatmentPublished)) ? (
                  <Button type="primary" onClick={() => setActiveTab("treatment")}>
                    Open risk treatment plan
                  </Button>
                ) : null}
              </div>
            ) : null}
            <Text type="secondary" className="mt-3 block text-xs">
              Onboarding risk scale: {riskScale}-point for severity and likelihood. Consequence is the CIA+P binary sum
              (0–4). Risk register score is severity rank + likelihood rank + consequence sum. Control strength (1–5) is
              optional in the Edit drawer and is not part of the score. Significance is Significant vs Not significant using register-only sum
              thresholds (Project Onboarding still uses its separate product benchmark elsewhere). Wazuh / telemetry
              inputs are not wired yet (reserved).
            </Text>
            {riskPkg.lastGeneratedAt ? (
              <Text type="secondary" className="mt-1 block text-xs">
                Last AI run: {formatDdMmYyyyTime(riskPkg.lastGeneratedAt)}
              </Text>
            ) : null}
          </div>

          <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3 shadow-sm">
            <Space wrap size="middle" className="w-full">
              <Input.Search
                allowClear
                placeholder="Search register…"
                value={registerSearchText}
                onChange={(e) => setRegisterSearchText(e.target.value)}
                style={{ minWidth: 200, maxWidth: 320 }}
              />
              <Select
                allowClear
                placeholder="Lifecycle"
                className="min-w-[140px]"
                value={registerFilterLifecycle || undefined}
                onChange={(v) => setRegisterFilterLifecycle(v ?? "")}
                options={registerFacetOptions.lifecycles.map((v) => ({ value: v, label: v }))}
              />
              <Button
                type="link"
                className="px-0"
                onClick={() => {
                  setRegisterSearchText("");
                  setRegisterFilterLifecycle("");
                  setRegisterTableResetKey((k) => k + 1);
                }}
              >
                Clear filters & column filters
              </Button>
            </Space>
            <Text type="secondary" className="mt-2 block text-xs">
              Showing {filteredRegisterRows.length} of {visibleRows.length} rows (after search & lifecycle). Use column
              headers to sort or filter Category, Source, and Domain. Drag headers to reorder; drag the right edge to
              resize.
            </Text>
          </div>

          <div className="flex min-h-[600px] flex-col">
            <DndContext sensors={tableSensors} collisionDetection={closestCenter} onDragEnd={onRegisterColumnDragEnd}>
              <SortableContext items={registerColumnOrder} strategy={horizontalListSortingStrategy}>
                <Table
                  key={registerTableResetKey}
                  size="middle"
                  rowKey="rowUid"
                  tableLayout="fixed"
                  scroll={{ x: registerTableScrollX, y: "min(72vh, 820px)" }}
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200],
                  }}
                  rowClassName={(_, index) => (index % 2 === 1 ? "bg-[color:var(--color-surface-sunken)]" : "")}
                  components={registerTableComponents}
                  dataSource={filteredRegisterRows}
                  columns={columns}
                  className="risk-register-table [&_.ant-table-thead>tr>th]:bg-[color:var(--color-bg-alt)] [&_.ant-table-thead>tr>th]:font-semibold"
                />
              </SortableContext>
            </DndContext>
          </div>

          <Modal
            title={cellDetail?.title}
            open={!!cellDetail}
            onCancel={() => setCellDetail(null)}
            footer={null}
            destroyOnClose
            width={560}
          >
            <pre className="m-0 max-h-[min(60vh,480px)] overflow-y-auto whitespace-pre-wrap font-sans text-sm text-[color:var(--color-text-hero)]">
              {cellDetail?.text}
            </pre>
          </Modal>

          <Drawer
            title={
              drawerRow?.lifecycle === "manual" && !String(drawerRow?.riskTitle || "").trim()
                ? "Add manual risk"
                : "Risk row"
            }
            width={520}
            open={!!drawerRow}
            onClose={() => setDrawerRow(null)}
            extra={
              <Space>
                <Button onClick={() => setDrawerRow(null)}>Cancel</Button>
                <Button type="primary" disabled={!canEditRiskMatrix} onClick={() => void saveDrawer()}>
                  Save
                </Button>
              </Space>
            }
          >
            {drawerRow ? (
              <div className="flex max-h-[calc(100vh-120px)] flex-col gap-3 overflow-y-auto pr-1">
                <div className="text-[11px] font-semibold text-[color:var(--color-text-muted-dark)]">
                  Fields below use the same names as the risk register columns.
                </div>

                <label className="block">
                  <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Title</div>
                  <TextArea
                    rows={2}
                    value={drawerRow.riskTitle ?? ""}
                    disabled={!canEditRiskMatrix}
                    onChange={(e) => setDrawerRow({ ...drawerRow, riskTitle: e.target.value })}
                  />
                </label>

                <label className="block">
                  <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Department</div>
                  <TextArea
                    rows={2}
                    value={drawerRow.department ?? ""}
                    disabled={!canEditRiskMatrix}
                    onChange={(e) => setDrawerRow({ ...drawerRow, department: e.target.value })}
                  />
                </label>

                <label className="block">
                  <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Owner</div>
                  <Select
                    className="w-full"
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    disabled={!canEditRiskMatrix || ownerSelectOptions.length === 0}
                    placeholder={
                      ownerSelectOptions.length === 0
                        ? "No project members"
                        : drawerRow.riskOwner && !drawerRow.riskOwnerUserId
                          ? drawerRow.riskOwner
                          : OWNER_NOT_ASSIGNED
                    }
                    value={drawerRow.riskOwnerUserId || undefined}
                    options={ownerSelectOptions}
                    onChange={(v) => {
                      const m = projectMembers.find((x) => x.id === v);
                      setDrawerRow({
                        ...drawerRow,
                        riskOwnerUserId: v || undefined,
                        riskOwner: v && m ? m.name : drawerRow.riskOwner || "",
                      });
                    }}
                  />
                </label>

                {[
                  ["riskCategory", "Category"],
                  ["riskSource", "Source"],
                  ["riskDomain", "Domain"],
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">{label}</div>
                    <TextArea
                      rows={2}
                      value={drawerRow[key] ?? ""}
                      disabled={!canEditRiskMatrix}
                      onChange={(e) => setDrawerRow({ ...drawerRow, [key]: e.target.value })}
                    />
                  </label>
                ))}

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Sev</div>
                    <Tooltip
                      title={
                        <pre className="m-0 whitespace-pre-wrap font-sans text-xs">
                          {formatSeverityRankTooltip(riskScale, drawerRow.severityRank, drawerRow.severityBands)}
                        </pre>
                      }
                    >
                      <InputNumber
                        className="w-full"
                        min={1}
                        max={rankMax}
                        value={drawerRow.severityRank}
                        disabled={!canEditRiskMatrix}
                        onChange={(v) =>
                          setDrawerRow({
                            ...drawerRow,
                            severityRank: v ?? 1,
                          })
                        }
                      />
                    </Tooltip>
                  </label>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Like</div>
                    <Tooltip
                      title={
                        <pre className="m-0 whitespace-pre-wrap font-sans text-xs">
                          {formatLikelihoodRankTooltip(riskScale, drawerRow.likelihoodRank, drawerRow.likelihoodBand)}
                        </pre>
                      }
                    >
                      <InputNumber
                        className="w-full"
                        min={1}
                        max={rankMax}
                        value={drawerRow.likelihoodRank}
                        disabled={!canEditRiskMatrix}
                        onChange={(v) =>
                          setDrawerRow({
                            ...drawerRow,
                            likelihoodRank: v ?? 1,
                          })
                        }
                      />
                    </Tooltip>
                  </label>
                </div>

                <div>
                  <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Cons</div>
                  <p className="mb-2 mt-0.5 text-[11px] font-medium text-[color:var(--color-text-muted)]">
                    CIA+P (Y/N per dimension) - drives the consequence value in the Cons column.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["confidentialityAffected", "Confidentiality"],
                      ["integrityAffected", "Integrity"],
                      ["availabilityAffected", "Availability"],
                      ["privacyAffected", "Privacy"],
                    ].map(([key, label]) => (
                      <label key={key} className="block">
                        <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">{label}</div>
                        <Select
                          className="w-full"
                          value={drawerRow[key] ?? "N"}
                          disabled={!canEditRiskMatrix}
                          onChange={(v) => setDrawerRow({ ...drawerRow, [key]: v })}
                          options={[
                            { value: "N", label: "N" },
                            { value: "Y", label: "Y" },
                          ]}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-medium text-[color:var(--color-text-hero)]">
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <span>
                      <span className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Score</span>{" "}
                      <strong className="text-[color:var(--color-text-hero)]">{drawerPreviewScore.riskScore}</strong>
                    </span>
                    <span>
                      <span className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Significance</span>{" "}
                      <strong className="text-[color:var(--color-text-hero)]">{drawerPreviewScore.riskSignificanceCategory}</strong>
                    </span>
                    <span>
                      <span className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Cons</span> (sum){" "}
                      <strong className="text-[color:var(--color-text-hero)]">{ciaConsequenceBinarySumFromRow(drawerRow)}</strong>
                    </span>
                  </div>
                </div>

                <div className="mt-2 border-t border-[color:var(--color-border)] pt-3">
                  <div className="mb-2 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-text-muted)]">
                    Additional details
                  </div>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">Risk description</div>
                    <TextArea
                      rows={4}
                      value={drawerRow.riskDescription ?? ""}
                      disabled={!canEditRiskMatrix}
                      onChange={(e) => setDrawerRow({ ...drawerRow, riskDescription: e.target.value })}
                    />
                  </label>
                  {[
                    ["assetId", "Asset ID"],
                    ["assetName", "Asset name"],
                    ["linkedEvidence", "Linked evidence"],
                    ["customerImpact", "Customer impact"],
                    ["financialImpact", "Financial impact"],
                    ["healthSafety", "Health and safety"],
                    ["impactOnReputation", "Impact on reputation"],
                    ["legalImpact", "Legal impact"],
                    ["confidentiality", "Confidentiality (narrative)"],
                    ["integrity", "Integrity (narrative)"],
                    ["availability", "Availability (narrative)"],
                    ["privacy", "Privacy (narrative)"],
                    ["existingControl", "Existing control"],
                  ].map(([key, label]) => (
                    <label key={key} className="mt-3 block">
                      <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">{label}</div>
                      <TextArea
                        rows={key === "existingControl" ? 4 : 2}
                        value={drawerRow[key] ?? ""}
                        disabled={!canEditRiskMatrix}
                        onChange={(e) => setDrawerRow({ ...drawerRow, [key]: e.target.value })}
                      />
                    </label>
                  ))}
                  <label className="mt-3 block">
                    <div className="text-[10px] font-bold uppercase text-[color:var(--color-text-muted)]">
                      Control strength (1–5, optional)
                    </div>
                    <InputNumber
                      className="w-full"
                      min={1}
                      max={5}
                      value={drawerRow.existingControlStrength}
                      disabled={!canEditRiskMatrix}
                      onChange={(v) =>
                        setDrawerRow({
                          ...drawerRow,
                          existingControlStrength: v ?? 1,
                        })
                      }
                    />
                  </label>
                </div>

                <Text type="secondary" className="text-xs">
                  Source refs (read-only):{" "}
                  {(drawerRow.sourceRefs || [])
                    .map((s) => `${s.type}:${s.ref}`)
                    .join(" · ") || "-"}
                </Text>
              </div>
            ) : null}
          </Drawer>
        </>
      )}
    </div>
  );

  const soaColumns = useMemo(
    () => [
      {
        title: "Control ID",
        dataIndex: "controlIdDisplay",
        key: "controlIdDisplay",
        width: 108,
        fixed: "left",
        render: (t) => <span className="font-semibold text-[color:var(--color-accent)]">{t || "-"}</span>,
      },
      {
        title: "Control title",
        dataIndex: "controlName",
        key: "controlName",
        width: 220,
        ellipsis: true,
      },
      {
        title: "Applicable",
        dataIndex: "applicable",
        key: "applicable",
        width: 130,
        render: (v, row) => (
          <div className="flex flex-col gap-1">
          <SoaApplicabilityBadge value={v} />
          <Select
            className="w-full min-w-[72px]"
            size="small"
            value={v === "Y" || v === "N" ? v : undefined}
            placeholder="Decide"
            disabled={!canEditSoaRows}
            options={[
              { value: "Y", label: "Applicable" },
              { value: "N", label: "Not applicable" },
            ]}
            onChange={(next) => {
              const nextRows = soaRowsRef.current.map((r) =>
                r.rowUid === row.rowUid ? { ...r, applicable: next } : r,
              );
              setSoaRows(nextRows);
              void persistSoaPackage(nextRows);
            }}
          />
          </div>
        ),
      },
      {
        title: "Justification",
        dataIndex: "justification",
        key: "justification",
        width: 220,
        render: (v, row) => (
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 5 }}
            value={v ?? ""}
            disabled={!canEditSoaRows}
            onChange={(e) =>
              setSoaRows((prev) =>
                prev.map((r) => (r.rowUid === row.rowUid ? { ...r, justification: e.target.value } : r)),
              )
            }
            onBlur={() => void persistSoaRowsFromRef()}
          />
        ),
      },
      {
        title: "Implementation",
        dataIndex: "implementation",
        key: "implementation",
        width: 220,
        render: (v, row) => (
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 5 }}
            value={v ?? ""}
            disabled={!canEditSoaRows}
            onChange={(e) =>
              setSoaRows((prev) =>
                prev.map((r) => (r.rowUid === row.rowUid ? { ...r, implementation: e.target.value } : r)),
              )
            }
            onBlur={() => void persistSoaRowsFromRef()}
          />
        ),
      },
      {
        title: "Last review",
        dataIndex: "lastReview",
        key: "lastReview",
        width: 120,
        render: (v, row) => (
          <Input
            size="small"
            placeholder="YYYY-MM-DD"
            value={v ?? ""}
            disabled={!canEditSoaRows}
            onChange={(e) =>
              setSoaRows((prev) =>
                prev.map((r) => (r.rowUid === row.rowUid ? { ...r, lastReview: e.target.value } : r)),
              )
            }
            onBlur={() => void persistSoaRowsFromRef()}
          />
        ),
      },
    ],
    [canEditSoaRows, persistSoaPackage, persistSoaRowsFromRef],
  );

  const soaPanel = (
    <div className="space-y-6">
      <SoaApprovalPanel
        pkg={soaBasePkg}
        rows={soaRows}
        isLeadAdvisor={isStrictLeadAdvisor}
        onApprove={onApproveSoa}
        onReopen={onReopenSoa}
      />
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          {canEditSoa ? (
            <>
              <Button type="primary" loading={syncingSoa} disabled={soaApproved} onClick={() => void onSyncSoaFromTreatment()}>
                Sync from treatment plan
              </Button>
              <Button
                loading={generatingSoaDetails}
                disabled={soaRows.length === 0 || soaApproved}
                onClick={() => void onGenerateSoaDetails()}
              >
                Generate SOA details (AI)
              </Button>
            </>
          ) : null}
        </div>
        <Text type="secondary" className="mt-3 block text-xs">
          Control ID, title, and applicability are taken from Annex A controls on the published treatment plan.
          Justification, implementation, and last review are filled by AI (project advisor) or edited inline.
        </Text>
        {soaBasePkg.lastSyncedFromTreatmentAt ? (
          <Text type="secondary" className="mt-2 block text-xs">
            Last sync from treatment: {formatDdMmYyyyTime(soaBasePkg.lastSyncedFromTreatmentAt)}
          </Text>
        ) : null}
        {soaBasePkg.lastAiDetailsAt ? (
          <Text type="secondary" className="mt-1 block text-xs">
            Last AI narrative run: {formatDdMmYyyyTime(soaBasePkg.lastAiDetailsAt)}
          </Text>
        ) : null}
        {canEditSoa && soaApproved ? (
          <Text className="mt-2 block text-xs font-semibold text-[color:var(--color-text-muted-dark)]" data-testid="soa-locked-note">
            Locked - this Statement of Applicability is approved. Sync, Generate details and edits are disabled until the Lead Advisor reopens it for correction.
          </Text>
        ) : null}
        {!canEditSoa ? (
          <Text className="mt-2 block text-xs font-semibold text-[color:var(--color-text-muted-dark)]">
            Read-only - only the project advisor can sync, run AI, or edit cells.
          </Text>
        ) : null}
      </div>
      <SoaUnsetNotice controls={listUnsetSoaControls(soaRows)} />
      <div className="flex items-center justify-end text-xs font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {soaRows.length} control{soaRows.length === 1 ? "" : "s"}
      </div>
      <div className="flex min-h-[480px] flex-col">
        <Table
          size="middle"
          rowKey="rowUid"
          tableLayout="fixed"
          scroll={{ x: 1180, y: "min(72vh, 820px)" }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
          }}
          dataSource={soaRows}
          columns={soaColumns}
          locale={{ emptyText: "No controls yet. The advisor can sync from the published treatment plan." }}
          className="[&_.ant-table-thead>tr>th]:bg-[color:var(--color-accent-fill)] [&_.ant-table-thead>tr>th]:text-white [&_.ant-table-thead>tr>th]:font-semibold"
          rowClassName={(_, index) => (index % 2 === 1 ? "bg-[color:var(--color-surface-sunken)]" : "")}
        />
      </div>
    </div>
  );

  const soaStaleWarning = soaPostApprovalEditsText(soaBasePkg);
  const treatmentPanel = (
    <div className="space-y-6">
      {soaStaleWarning ? (
        <Alert type="warning" showIcon data-testid="soa-stale-treatment-warning" message={soaStaleWarning} />
      ) : null}
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="primary"
            loading={generatingTreatment}
            disabled={!canGenerateTreatment}
            onClick={() => void onGenerateTreatment()}
          >
            Generate treatment plan with AI
          </Button>
          {treatmentPkg.lastGeneratedAt ? (
            <Text type="secondary" className="text-xs">
              Last AI run: {formatDdMmYyyyTime(treatmentPkg.lastGeneratedAt)}
            </Text>
          ) : null}
        </div>
        <Text type="secondary" className="mt-3 block text-xs">
          One plan row per accepted or manual risk. The model proposes assets, controls, residual ratings (same S+L+C
          math as the register), strategy, timelines, and frequency. Regenerate replaces AI fields; adjust strategy,
          acceptable, dates, recurrence, and frequency inline.
        </Text>
        {!canGenerateTreatment && registerFinalized && isAdvisorPortal ? (
          <Text className="mt-2 block text-xs font-semibold text-[color:var(--color-warn-fg)]">
            No active risks are in scope for treatment (accepted, manual, or non-rejected register rows with a title or
            description). If the register looks correct after approval, refresh the page to reload data.
          </Text>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            type="primary"
            loading={publishingTreatment}
            disabled={
              !isAdvisorPortal ||
              !registerFinalized ||
              !treatmentPkg.lastGeneratedAt ||
              treatmentRows.length === 0 ||
              treatmentPublished
            }
            onClick={() => void onPublishTreatment()}
          >
            {treatmentPublished
              ? "Treatment plan shared (read-only for company)"
              : "Share treatment plan with company & Lead Advisor (unlocks SOA)"}
          </Button>
          {treatmentPublished && treatmentPkg.publishedAt ? (
            <Text type="secondary" className="text-xs">
              Published {formatDdMmYyyyTime(treatmentPkg.publishedAt)}
            </Text>
          ) : null}
        </div>
        {treatmentPublished && canNavigateToSoa ? (
          <div className="mt-4">
            <Button type="default" onClick={() => setActiveTab("soa")}>
              Continue to Statement of applicability
            </Button>
          </div>
        ) : null}
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3 shadow-sm">
        <Space wrap size="middle" className="w-full">
          <Input.Search
            allowClear
            placeholder="Search treatment plan…"
            value={treatmentSearchText}
            onChange={(e) => setTreatmentSearchText(e.target.value)}
            style={{ minWidth: 200, maxWidth: 320 }}
          />
          <Button
            type="link"
            className="px-0"
            onClick={() => {
              setTreatmentSearchText("");
              setTreatmentTableResetKey((k) => k + 1);
            }}
          >
            Clear search & column filters
          </Button>
        </Space>
        <Text type="secondary" className="mt-2 block text-xs">
          Showing {filteredTreatmentRows.length} of {treatmentTableData.length} rows (after search). Use column headers
          to sort or filter (Category, Strategy, Acceptable). Drag headers to reorder; drag the header edge to resize.
        </Text>
      </div>
      <div className="flex min-h-[480px] flex-col">
        <DndContext sensors={tableSensors} collisionDetection={closestCenter} onDragEnd={onTreatmentColumnDragEnd}>
          <SortableContext items={treatmentColumnOrder} strategy={horizontalListSortingStrategy}>
            <Table
              key={treatmentTableResetKey}
              size="middle"
              rowKey="rowUid"
              tableLayout="fixed"
              scroll={{ x: treatmentTableScrollX, y: "min(72vh, 820px)" }}
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100, 200],
              }}
              rowClassName={(_, index) => (index % 2 === 1 ? "bg-[color:var(--color-surface-sunken)]" : "")}
              components={treatmentTableComponents}
              dataSource={filteredTreatmentRows}
              columns={treatmentColumns}
              className="[&_.ant-table-thead>tr>th]:bg-[color:var(--color-bg-alt)] [&_.ant-table-thead>tr>th]:font-semibold"
            />
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <StageHeader
        title="Risk Analysis"
        actions={riskContextLine ? (
          <p className="text-right text-sm font-medium text-[color:var(--color-text-muted)] sm:max-w-md">{riskContextLine}</p>
        ) : null}
      />
      <p className="max-w-3xl text-sm font-medium text-[color:var(--color-text-muted-dark)]">
        Build the risk register from gap analysis, onboarding issues, and optional master data. Accept, reject, or add rows
        manually before downstream treatment planning.
      </p>

      <RiskAnalysisStatusBar
        activeTab={activeTab}
        treatmentUnlocked={treatmentStepUnlocked}
        soaUnlocked={soaStepUnlocked}
        registerFinalized={registerFinalized}
        treatmentPublished={treatmentPublished}
        soaPhaseComplete={soaPhaseComplete}
      />

      <SubStepNav
        ariaLabel="Risk analysis sections"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        steps={RISK_ANALYSIS_SUB_NAV.map((s) => {
          const locked =
            (s.key === "treatment" && !treatmentStepUnlocked) || (s.key === "soa" && !soaStepUnlocked);
          let lockedHint;
          if (locked) {
            if (s.key === "soa") {
              lockedHint = registerFinalized
                ? "Available after the project advisor shares the treatment plan with the company."
                : "Available after the risk register is finalized and the treatment plan is shared.";
            } else {
              lockedHint = "Available after the Lead Advisor approves the risk register.";
            }
          }
          const completed =
            (s.key === "register" && registerFinalized) ||
            (s.key === "treatment" && treatmentPublished) ||
            (s.key === "soa" && soaPhaseComplete);
          return { ...s, locked, lockedHint, completed };
        })}
      />

      {activeTab === "register" ? registerPanel : null}
      {activeTab === "treatment" ? treatmentPanel : null}
      {activeTab === "soa" ? soaPanel : null}
    </div>
  );
}
